import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';

function SummaryCard({ title, value, body }) {
  return (
    <div className="rounded-3xl border border-[#f5b221]/16 bg-[linear-gradient(180deg,#161616,#090909)] p-5">
      <p className="text-sm uppercase text-gray-400">{title}</p>
      <p className="mt-3 text-4xl font-bold text-[#f5b221]">{value}</p>
      <p className="mt-2 text-sm text-gray-500">{body}</p>
    </div>
  );
}

export default function MonetizationReadiness() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await api.get('/admin/platform/monetization-readiness');
        setData(response.data);
      } catch (err) {
        setError(err?.response?.data?.message || 'Could not load monetization readiness');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const summary = data?.summary || {};

  return (
    <Layout>
      <div className="space-y-8">
        <section className="rounded-3xl border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top_left,rgba(245,178,33,0.16),transparent_34%),linear-gradient(180deg,#171717,#090909)] p-6">
          <p className="text-sm uppercase tracking-[0.2em] text-[#f7d88a]">Monetization</p>
          <h2 className="mt-3 text-3xl font-semibold text-white">Plan readiness and renewal pressure</h2>
          <p className="mt-2 max-w-3xl text-sm text-gray-400">
            This page keeps business profile adoption, renewal pressure, and paid-tier readiness visible on the web admin side while the app gets its own clean workspace screen.
          </p>
        </section>

        {error ? <p className="text-sm text-red-400">{error}</p> : null}

        {loading ? (
          <section className="rounded-3xl border border-gray-800 bg-gray-950 p-6">
            <p className="text-sm text-gray-400">Loading monetization readiness...</p>
          </section>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
              <SummaryCard title="Business profile completion" value={`${summary.businessProfileCompletionPercent || 0}%`} body={`${summary.completedBusinessProfiles || 0} completed business profiles`} />
              <SummaryCard title="Renewals in 30d" value={summary.renewalsNext30d || 0} body="Connected systems requiring near-term attention" />
              <SummaryCard title="Open billing cases" value={summary.openBillingCases || 0} body="Support cases tied to billing or licensing" />
              <SummaryCard title="Tracked tiers" value={(summary.tierDistribution || []).length} body="Distinct plan tiers currently visible" />
            </div>

            <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
              <div className="rounded-3xl border border-gray-800 bg-gray-950 p-6">
                <h3 className="text-xl font-semibold text-white">Renewal watchlist</h3>
                <div className="mt-5 space-y-3">
                  {(data?.renewals || []).length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-gray-700 p-5 text-sm text-gray-400">
                      No renewals are due in the next 30 days.
                    </div>
                  ) : (
                    (data?.renewals || []).map((item) => (
                      <div key={item.id} className="rounded-2xl border border-gray-800 bg-black/50 p-4">
                        <p className="font-semibold text-white">{item.name}</p>
                        <p className="mt-2 text-sm text-gray-400">
                          {item.planName || 'Unknown plan'} · {item.licenseStatus || 'Unknown license'} · {item.status || 'Unknown status'}
                        </p>
                        <p className="mt-2 text-sm text-[#f7d88a]">
                          {item.daysToRenewal != null ? `${item.daysToRenewal} days to renewal` : 'No renewal date'}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-3xl border border-gray-800 bg-gray-950 p-6">
                <h3 className="text-xl font-semibold text-white">Tier distribution</h3>
                <div className="mt-5 space-y-3">
                  {(summary.tierDistribution || []).map((item) => (
                    <div key={item._id || 'unknown'} className="rounded-2xl border border-gray-800 bg-black/50 px-4 py-3 text-sm text-gray-300">
                      {item._id || 'unset'} · {item.total}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="rounded-3xl border border-gray-800 bg-gray-950 p-6">
              <h3 className="text-xl font-semibold text-white">Recommended rollout path</h3>
              <div className="mt-5 space-y-3">
                {(data?.opportunities || []).map((line) => (
                  <div key={line} className="rounded-2xl border border-gray-800 bg-black/50 px-4 py-3 text-sm text-gray-300">
                    {line}
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
}
